import { Button, Menu, MenuItem, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { CalendarTodayOutlined } from "@mui/icons-material";

import FilterLoading from "./filterloading";
import FilterSuccess from "./filtersuccess";
import CustomDateRangeDialog from "./customdaterangedialog";
import { DateFilterProps, DateRange } from "../api/types";

const options = [
  "Today",
  "Last Week",
  "Last Month",
  "3 Months Ago",
  "6 Months Ago",
  "A Year Ago",
  "Custom",
];

const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

const getButtonLabel = (value: string, customRange: DateRange | null) => {
  if (value === "Custom" && customRange) {
    return `${formatDate(customRange.startDate)} → ${formatDate(
      customRange.endDate,
    )}`;
  }
  return value;
};

const getDateRange = (option: string): DateRange | null => {
  const now = new Date();
  const endDate = new Date(now);

  const startOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const startOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay() || 7;
    d.setDate(d.getDate() - day + 1);
    return startOfDay(d);
  };

  const endOfWeek = (date: Date) => {
    const d = startOfWeek(date);
    d.setDate(d.getDate() + 6);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  switch (option) {
    case "Today":
      return { startDate: startOfDay(now), endDate };

    case "Last Week": {
      const lastWeek = new Date(now);
      lastWeek.setDate(lastWeek.getDate() - 7);
      return {
        startDate: startOfWeek(lastWeek),
        endDate: endOfWeek(lastWeek),
      };
    }

    case "Last Month": {
      const year = now.getFullYear();
      const month = now.getMonth() - 1;
      return {
        startDate: new Date(year, month, 1),
        endDate: new Date(year, month + 1, 0, 23, 59, 59, 999),
      };
    }

    case "3 Months Ago":
      return {
        startDate: new Date(
          now.getFullYear(),
          now.getMonth() - 3,
          now.getDate(),
        ),
        endDate,
      };

    case "6 Months Ago":
      return {
        startDate: new Date(
          now.getFullYear(),
          now.getMonth() - 6,
          now.getDate(),
        ),
        endDate,
      };

    case "A Year Ago":
      return {
        startDate: new Date(
          now.getFullYear() - 1,
          now.getMonth(),
          now.getDate(),
        ),
        endDate,
      };

    default:
      return null;
  }
};

const DateFilter = ({ onChange, walkthroughMenuOpen = false }: DateFilterProps & { walkthroughMenuOpen?: boolean }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [value, setValue] = useState("Last Month");
  const [customRange, setCustomRange] = useState<DateRange | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  const open = walkthroughMenuOpen ? true : Boolean(anchorEl);

  const runChange = (range: DateRange) => {
    setLoading(true);
    setShowSuccess(false);

    setTimeout(() => {
      setLoading(false);
      setShowSuccess(true);

      onChange({
        startDate: new Date(range.startDate.toISOString()),
        endDate: new Date(range.endDate.toISOString()),
      });

      setTimeout(() => setShowSuccess(false), 2500);
    }, 2500);
  };

  return (
    <>
      <Button
        id="dashboard-date-filter"
        ref={buttonRef}
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setShowSuccess(false);
        }}
        startIcon={<CalendarTodayOutlined />}
        sx={{
          width: 200,
          height: 50,
          justifyContent: "flex-start",
          textTransform: "none",
          borderRadius: "12px",
          px: 2,
          py: 1,
          marginRight: 2,
          backgroundColor: "#fff",
          color: "#0f172a",
          textAlign: "left",
          fontWeight: 500,
          boxShadow: "0px 2px 6px rgba(15, 23, 42, 0.12)",
          "& .MuiButton-startIcon svg": {
            fontSize: 24,
            color: "primary.main",
          },
          transition:
            "transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease",

          "&:hover": {
            transform: "scale(1.06)",
          },
        }}
      >
        <Typography fontSize={15} color="primary.main">
          {getButtonLabel(value, customRange)}
        </Typography>
      </Button>

      <Menu
        anchorEl={walkthroughMenuOpen ? buttonRef.current : anchorEl}
        open={open}
        onClose={walkthroughMenuOpen ? () => {} : () => setAnchorEl(null)}
        PaperProps={{
          id: "dashboard-date-filter-menu",
          sx: {
            borderRadius: 2,
            mt: -8,
            ml: 4,
            minWidth: 220,
            boxShadow: "0px 12px 40px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            selected={option === value}
            onClick={() => {
              setAnchorEl(null);

              if (option === value && option !== "Custom") return;

              if (option === "Custom") {
                setCustomOpen(true);
                return;
              }

              setCustomRange(null);
              setValue(option);

              const range = getDateRange(option);
              if (range) runChange(range);
            }}
            sx={{
              fontSize: 14,
              borderRadius: 2,
              px: 2,
              py: 1.25,
              mx: 0.5,
              my: 0.5,

              color: "#0f172a",

              "&:hover": {
                backgroundColor: "#f8fafc",
              },

              "&.Mui-selected": {
                backgroundColor: "#fff1f2",
                color: "#ff3b3b",
                fontWeight: 600,
              },

              "&.Mui-selected:hover": {
                backgroundColor: "#ffe4e6",
              },
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>

      <CustomDateRangeDialog
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        onApply={(range) => {
          setCustomOpen(false);
          setValue("Custom");
          setCustomRange(range);
          runChange(range);
        }}
      />

      {loading && (
        <FilterLoading displayLabel={getButtonLabel(value, customRange)} />
      )}

      <FilterSuccess
        open={showSuccess}
        displayLabel={getButtonLabel(value, customRange)}
      />
    </>
  );
};

export default DateFilter;
